import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Agent, UpdateAgentRequest, ProviderInfo } from '../../types/agent.js';
import * as agentApi from '../../services/agent-api.js';
import * as companyApi from '../../services/company-api.js';
import type { McpToolGroup } from '../../services/company-api.js';
import { navigateTo } from '../../services/router.js';
import '../settings/cp-soul-editor.js';
import './cp-agent-avatar-upload.js';

@customElement('cp-agent-detail')
export class CpAgentDetail extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) agent: Agent | null = null;

  @state() private _saving = false;
  @state() private _deleting = false;
  @state() private _confirmDelete = false;
  @state() private _error: string | null = null;
  @state() private _successMessage: string | null = null;

  // Editable fields (non-soul)
  @state() private _name = '';
  @state() private _provider = '';
  @state() private _model = '';
  @state() private _maxTokens = 4096;
  @state() private _color = '#6366f1';
  @state() private _providers: ProviderInfo[] = [];
  @state() private _modelsForProvider: string[] = [];

  // Tool configuration state
  @state() private _availableTools: McpToolGroup[] = [];
  @state() private _selectedTools: Set<string> = new Set();
  @state() private _expandedGroups: Set<string> = new Set();
  @state() private _toolsLoading = false;
  @state() private _toolsSaving = false;
  @state() private _toolsSuccess = '';

  updated(changed: Map<string, unknown>): void {
    if (changed.has('agent') && this.agent) {
      this._name = this.agent.name;
      this._provider = this.agent.provider ?? 'anthropic';
      this._model = this.agent.model ?? 'claude-sonnet-4-6';
      this._maxTokens = this.agent.max_tokens;
      this._color = this.agent.color;
      this._error = null;
      this._successMessage = null;
      this._confirmDelete = false;
      this._toolsSuccess = '';
      void this._loadProviders();
      void this._loadTools();
    }
  }

  private async _loadProviders(): Promise<void> {
    try {
      const res = await fetch('/api/providers', { credentials: 'include' });
      if (res.ok) {
        this._providers = await res.json() as ProviderInfo[];
      }
      await this._loadModelsForProvider(this._provider);
    } catch {
      this._providers = [];
    }
  }

  private async _loadModelsForProvider(providerId: string): Promise<void> {
    try {
      const res = await fetch(`/api/providers/${encodeURIComponent(providerId)}/models`, { credentials: 'include' });
      if (res.ok) {
        this._modelsForProvider = await res.json() as string[];
      }
    } catch {
      this._modelsForProvider = [];
    }
  }

  private async _loadTools(): Promise<void> {
    if (!this.agent) return;
    this._toolsLoading = true;
    try {
      if (this._availableTools.length === 0) {
        this._availableTools = await companyApi.getAvailableTools();
      }
      // Parse current allowed_tools — empty array means "unrestricted" (all tools selected)
      const currentTools: string[] = JSON.parse(this.agent.allowed_tools || '[]');
      if (currentTools.length === 0) {
        this._selectedTools = new Set(this._availableTools.flatMap(g => g.tools.map(t => t.fullName)));
      } else {
        this._selectedTools = new Set(currentTools);
      }
      this._expandedGroups = new Set();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden der Tools';
    } finally {
      this._toolsLoading = false;
    }
  }

  private _toggleToolGroup(server: string): void {
    const next = new Set(this._expandedGroups);
    if (next.has(server)) {
      next.delete(server);
    } else {
      next.add(server);
    }
    this._expandedGroups = next;
  }

  private _toggleTool(fullName: string): void {
    const next = new Set(this._selectedTools);
    if (next.has(fullName)) {
      next.delete(fullName);
    } else {
      next.add(fullName);
    }
    this._selectedTools = next;
    this._toolsSuccess = '';
  }

  private _toggleAllInGroup(group: McpToolGroup): void {
    const next = new Set(this._selectedTools);
    const allSelected = group.tools.every(t => next.has(t.fullName));
    for (const tool of group.tools) {
      if (allSelected) {
        next.delete(tool.fullName);
      } else {
        next.add(tool.fullName);
      }
    }
    this._selectedTools = next;
    this._toolsSuccess = '';
  }

  private async _saveAllowedTools(): Promise<void> {
    if (!this.agent || this._toolsSaving) return;
    this._toolsSaving = true;
    this._error = null;
    this._toolsSuccess = '';
    try {
      // Normalize: if all tools are selected, persist '[]' = "unrestricted"
      const allToolNames = this._availableTools.flatMap(g => g.tools.map(t => t.fullName));
      const allSelected = allToolNames.length > 0 && allToolNames.every(n => this._selectedTools.has(n));
      const toolsToSave = allSelected ? [] : Array.from(this._selectedTools);
      await companyApi.setAgentAllowedTools(this.agent.id, toolsToSave);
      this._toolsSuccess = 'Tools gespeichert';
      this._dispatchUpdated();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern der Tools';
    } finally {
      this._toolsSaving = false;
    }
  }

  private _onNameInput(e: Event): void {
    this._name = (e.target as HTMLInputElement).value;
    this._successMessage = null;
  }

  private async _onProviderChange(e: Event): Promise<void> {
    const newProvider = (e.target as HTMLSelectElement).value;
    this._provider = newProvider;
    this._successMessage = null;

    await this._loadModelsForProvider(newProvider);

    if (this._modelsForProvider.length > 0 && !this._modelsForProvider.includes(this._model)) {
      this._model = this._modelsForProvider[0];
    }
  }

  private _onModelChange(e: Event): void {
    this._model = (e.target as HTMLSelectElement).value;
    this._successMessage = null;
  }

  private _onMaxTokensInput(e: Event): void {
    this._maxTokens = Number((e.target as HTMLInputElement).value);
    this._successMessage = null;
  }

  private _onColorInput(e: Event): void {
    this._color = (e.target as HTMLInputElement).value;
    this._successMessage = null;
  }

  private async _saveSettings(): Promise<void> {
    if (!this.agent || this._saving) return;

    if (!this._name.trim()) {
      this._error = 'Name ist erforderlich';
      return;
    }

    this._saving = true;
    this._error = null;
    this._successMessage = null;

    try {
      const data: UpdateAgentRequest = {
        name: this._name.trim(),
        model: this._model,
        provider: this._provider || undefined,
        max_tokens: this._maxTokens,
        color: this._color,
      };

      await agentApi.updateAgent(this.agent.id, data);
      this._successMessage = 'Einstellungen gespeichert';
      this._dispatchUpdated();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private _handleDelete(): void {
    this._confirmDelete = true;
  }

  private _cancelDelete(): void {
    this._confirmDelete = false;
  }

  private async _confirmDeleteAgent(): Promise<void> {
    if (!this.agent) return;
    this._deleting = true;
    try {
      await agentApi.deleteAgent(this.agent.id);
      this.dispatchEvent(new CustomEvent('agent-deleted', { bubbles: true, composed: true }));
      navigateTo('/agents');
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
      this._confirmDelete = false;
    } finally {
      this._deleting = false;
    }
  }

  private _dispatchUpdated(): void {
    this.dispatchEvent(new CustomEvent('agent-updated', { bubbles: true, composed: true }));
  }

  render() {
    if (!this.agent) {
      return html`<div class="cp-agent-detail__loading">Lade Agent...</div>`;
    }
    const a = this.agent;

    return html`
      <div class="cp-agent-detail">
        ${this._renderHeader(a)}

        ${this._error ? html`<div class="cp-agent-detail__error">${this._error}</div>` : nothing}
        ${this._successMessage ? html`<div class="cp-agent-detail__success">${this._successMessage}</div>` : nothing}

        ${this._renderSettings(a)}

        ${this._renderTools()}

        <div class="cp-agent-detail__section">
          <cp-soul-editor .agentId=${a.id}></cp-soul-editor>
        </div>
      </div>
    `;
  }

  private _renderTools() {
    return html`
      <div class="cp-agent-detail__section">
        <h3 class="cp-agent-detail__section-title">MCP-Tools</h3>
        <p class="cp-agent-detail__tools-hint">
          Leer = alle Tools erlaubt. Ausgewaehlte Tools beschraenken den Agent auf diese Tools.
          Weniger Tools = schnellerer Chat-Start.
        </p>

        ${this._toolsSuccess ? html`<div class="cp-agent-detail__success">${this._toolsSuccess}</div>` : nothing}

        ${this._toolsLoading && this._availableTools.length === 0 ? html`
          <p class="cp-agent-detail__loading">Lade Tools...</p>
        ` : html`
          <div class="cp-agent-detail__tools-section">
            ${this._availableTools.filter(g => g.server !== 'builtin').map(group => {
              const allSelected = group.tools.every(t => this._selectedTools.has(t.fullName));
              const someSelected = group.tools.some(t => this._selectedTools.has(t.fullName));
              const isExpanded = this._expandedGroups.has(group.server);
              const selectedCount = group.tools.filter(t => this._selectedTools.has(t.fullName)).length;

              return html`
                <div class="cp-agent-detail__tools-group">
                  <button
                    class="cp-agent-detail__tools-group-header"
                    @click=${() => this._toggleToolGroup(group.server)}
                  >
                    <span class="cp-agent-detail__tools-group-title">
                      <span class="cp-agent-detail__tools-group-caret">${isExpanded ? '▼' : '▶'}</span>
                      <span>${group.server}</span>
                      <span class="cp-agent-detail__tools-group-count">(${selectedCount}/${group.tools.length})</span>
                    </span>
                    <label
                      class="cp-agent-detail__tools-group-all"
                      @click=${(e: Event) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        .checked=${allSelected}
                        .indeterminate=${someSelected && !allSelected}
                        @change=${() => this._toggleAllInGroup(group)}
                      />
                      <span>Alle</span>
                    </label>
                  </button>
                  ${isExpanded ? html`
                    <div class="cp-agent-detail__tools-list">
                      ${group.tools.map(tool => html`
                        <label class="cp-agent-detail__tool-item">
                          <input
                            type="checkbox"
                            .checked=${this._selectedTools.has(tool.fullName)}
                            @change=${() => this._toggleTool(tool.fullName)}
                          />
                          <span>${tool.name}</span>
                        </label>
                      `)}
                    </div>
                  ` : nothing}
                </div>
              `;
            })}
          </div>
        `}

        <button
          class="cp-agent-detail__save-btn"
          @click=${() => void this._saveAllowedTools()}
          ?disabled=${this._toolsSaving || this._toolsLoading}
        >${this._toolsSaving ? 'Speichert...' : 'Tools speichern'}</button>
      </div>
    `;
  }

  private _renderHeader(a: Agent) {
    return html`
      <div class="cp-agent-detail__header">
        <button class="cp-agent-detail__back-btn" @click=${() => navigateTo('/agents')}>
          ← Zurueck
        </button>
        <div class="cp-agent-detail__header-content">
          <cp-agent-avatar-upload
            .agentId=${a.id}
            .agentName=${a.name}
            .agentColor=${a.color || '#6366f1'}
            .hasAvatar=${!!a.avatar_mime_type}
            @avatar-uploaded=${() => this._dispatchUpdated()}
            @avatar-removed=${() => this._dispatchUpdated()}
          ></cp-agent-avatar-upload>
          <div class="cp-agent-detail__header-info">
            <h1 class="cp-agent-detail__name">${a.name}</h1>
            <span class="cp-agent-detail__meta">
              ${a.is_main ? 'Haupt-Agent' : a.personality_type || 'Agent'}
            </span>
          </div>
          ${!a.is_main ? html`
            <div class="cp-agent-detail__header-actions">
              <button class="cp-agent-detail__delete-btn" @click=${this._handleDelete}>Loeschen</button>
            </div>
          ` : nothing}
        </div>
        ${this._confirmDelete ? html`
          <div class="cp-agent-detail__confirm-bar">
            <span>Agent wirklich loeschen?</span>
            <div class="cp-agent-detail__confirm-bar-actions">
              <button class="cp-agent-detail__cancel-btn" @click=${this._cancelDelete} ?disabled=${this._deleting}>Abbrechen</button>
              <button class="cp-agent-detail__delete-btn" @click=${() => void this._confirmDeleteAgent()} ?disabled=${this._deleting}>
                ${this._deleting ? 'Loeschen...' : 'Loeschen'}
              </button>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderSettings(a: Agent) {
    return html`
      <div class="cp-agent-detail__section">
        <h3 class="cp-agent-detail__section-title">Einstellungen</h3>
        <div class="cp-agent-detail__settings-grid">
          <label class="cp-agent-detail__field">
            <span class="cp-agent-detail__label">Name</span>
            <input
              class="cp-agent-detail__input"
              type="text"
              .value=${this._name}
              @input=${this._onNameInput}
              required
            />
          </label>

          <label class="cp-agent-detail__field">
            <span class="cp-agent-detail__label">Farbe</span>
            <div class="cp-agent-detail__color-row">
              <input
                class="cp-agent-detail__color-input"
                type="color"
                .value=${this._color}
                @input=${this._onColorInput}
              />
              <span class="cp-agent-detail__color-value">${this._color}</span>
            </div>
          </label>

          <label class="cp-agent-detail__field">
            <span class="cp-agent-detail__label">Provider</span>
            <select
              class="cp-agent-detail__select"
              .value=${this._provider}
              @change=${this._onProviderChange}
            >
              ${this._providers.map(p => html`
                <option value=${p.id} ?selected=${this._provider === p.id}>${p.displayName}</option>
              `)}
            </select>
          </label>

          <label class="cp-agent-detail__field">
            <span class="cp-agent-detail__label">Modell</span>
            <select
              class="cp-agent-detail__select"
              .value=${this._model}
              @change=${this._onModelChange}
            >
              ${this._modelsForProvider.map(m => html`
                <option value=${m} ?selected=${this._model === m}>${m}</option>
              `)}
            </select>
          </label>

          <label class="cp-agent-detail__field">
            <span class="cp-agent-detail__label">Max Tokens</span>
            <input
              class="cp-agent-detail__input"
              type="number"
              min="256"
              max="32768"
              step="256"
              .value=${String(this._maxTokens)}
              @input=${this._onMaxTokensInput}
            />
          </label>
        </div>

        <button
          class="cp-agent-detail__save-btn"
          @click=${() => void this._saveSettings()}
          ?disabled=${this._saving}
        >${this._saving ? 'Speichert...' : 'Einstellungen speichern'}</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-detail': CpAgentDetail;
  }
}
